import { successMessage, failMessage } from "../../Client/MessagesActions"
import { MainThunkDispatch } from "../../../Reducers"
import { JobAPIType } from "../../Types"
import { apiEndpoints } from "../../../Routes/api"
import { safePromise } from "../../../Helpers/Functions/promise"
import { apiFetch, killPoll, pollAPI } from "../../Client/NetworkActions"
import { APIJobListParams } from "../../../Models/API/JobModel"
import { APIAppAsyncOperationModel } from "../../../Models/API/APIAppAsyncOperationModel"
import { AppDownloadSettings } from "../../../Models/API/AppSettings"

interface DownloadJobResponse {
    operationId: number
}

export const downloadAllResponses =
    (
        appID: string,
        args: Omit<APIJobListParams, "sortBy" | "orderBy">,
        downloadSettings: AppDownloadSettings,
        csv?: boolean,
        jobIds?: string[],
        fileName?: string
    ) =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.downloadAllJobResponse({
            appID,
            ...args,
            csv,
            jobIds,
            downloadSettings,
            fileName
        })
        const { error, data } = await safePromise<DownloadJobResponse>(
            dispatch(
                apiFetch(`DOWNLOAD_JOB_RESPONSE_${appID}`, endpoint, {
                    method: "POST"
                })
            )
        )

        if (error !== undefined) {
            dispatch(failMessage("Could not download data", error))
            return
        }

        if (!data) return

        let successText = `A download link will be sent to your registered email address shortly`
        if (!downloadSettings.sendEmail) {
            successText = `Your data will be ready shortly`
        }
        dispatch(successMessage(successText))

        dispatch({
            type: JobAPIType.downloadOperationId,
            meta: { appID },
            payload: data.operationId
        })
    }

export const getDownloadAllLink =
    (appID: string, filename: string) =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getDownloadAllJobResponseLink({
            filename
        })
        const { error, data } = await safePromise(
            dispatch(
                apiFetch(
                    `GET_DOWNLOAD_ALL_JOB_RESPONSE_LINK_${filename}`,
                    endpoint
                )
            )
        )
        const expires = Date.now() + 1000 * 60 * 60

        if (
            error !== undefined ||
            data === undefined ||
            data.link === undefined
        ) {
            if (
                error !== undefined ||
                (data !== undefined && data.link === undefined)
            ) {
                dispatch(failMessage("Could not download data", error))
            }

            return
        }

        dispatch({
            type: JobAPIType.download,
            meta: { appID },
            payload: {
                link: data.link,
                expires,
                ref: filename
            }
        })
    }

export const clearDownloadAllLink =
    (appID: string) => async (dispatch: MainThunkDispatch) =>
        dispatch({
            type: JobAPIType.clearDownload,
            payload: appID
        })

export const startMonitorAsyncOperation =
    (appID: string, id: number, pollInterval = 5000) =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getAsyncOperation({
            appID,
            id
        })
        const handleResponse = async (
            data: APIAppAsyncOperationModel | undefined
        ) => {
            if (data === undefined) {
                return
            }

            dispatch({
                type: JobAPIType.setDownloadOperation,
                meta: { appID },
                payload: data
            })
        }

        dispatch(
            pollAPI<APIAppAsyncOperationModel>(
                `ASYNC_OPERATION_POLL_${appID}_${id}`,
                endpoint,
                handleResponse,
                undefined,
                pollInterval
            )
        )
    }

export const stopMonitorAsyncOperation =
    (appID: string, id: number) => async (dispatch: MainThunkDispatch) => {
        dispatch(killPoll(`ASYNC_OPERATION_POLL_${appID}_${id}`))
    }

interface OperationDownloadUrlResponse {
    downloadUrl: string
}

export const getOperationDownloadURL =
    (appID: string, id: number) => async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getOperationDownloadURL({
            appID,
            id
        })

        const { data, error } = await safePromise<OperationDownloadUrlResponse>(
            dispatch(
                apiFetch(
                    `OPERATION_DOWNLOAD_URL_FETCH_${appID}_${id}`,
                    endpoint,
                    {
                        method: "POST"
                    }
                )
            )
        )

        if (error !== undefined) {
            throw error
        }

        if (data === undefined) {
            return undefined
        }

        return data.downloadUrl
    }
